import { createSlice } from "@reduxjs/toolkit";

import { CUSTOM_LEAD_FORM_VARIANT } from "@/constants/leadFormSurface";
import { isRenderableLeadFormConfig, type PublicLeadFormConfig } from "@/types/leadForm";

/**
 * Derive the variant hosts render.
 *
 * Runs from BOTH reducers so the result never depends on which arrives first —
 * ClientWrapper writes the legacy variant on mount, NewPage writes the config in its own
 * effect, and neither can rely on ordering.
 *
 * Only an INLINE config takes the slot: a popup config belongs to OpenLeadDrop, and
 * letting it win here would put a dialog layout inside a page column.
 */
type LeaddropFormState = {
  variant: string | null;
  legacy_variant: string | null;
  lead_form_config: PublicLeadFormConfig | null;
  lead_form_popup_config: PublicLeadFormConfig | null;
};

function recomputeVariant(state: LeaddropFormState) {
  const config = state.lead_form_config;
  const usesCustom =
    isRenderableLeadFormConfig(config) && config.form_type !== "popup";
  state.variant = usesCustom ? CUSTOM_LEAD_FORM_VARIANT : state.legacy_variant;
}

export const leaddropformSlice = createSlice({
  name: "leaddropform",
  initialState: {
    lead_drop_form: null,
    /**
     * What every host widget resolves against `LEAD_DROP_FORM_SECTION_VARIANTS`.
     *
     * DERIVED, not raw: it is the custom-form sentinel when a renderable inline config
     * applies to this page, and the university's legacy variant otherwise. Hosts stay
     * completely unaware of custom forms — see constants/leadFormSurface.ts.
     */
    variant: null as string | null,
    /**
     * The university's configured design variant, kept separately so `variant` can be
     * derived without destroying the fallback. This is what renders when no custom form
     * applies — i.e. everywhere, today.
     */
    legacy_variant: null as string | null,
    /**
     * The CMS-authored form resolved for THIS page (page pin → course → university
     * default). `null` means no config applies and the legacy variant renders — which is
     * the steady state everywhere today, so this is additive and cannot change an existing
     * page's behaviour on its own.
     *
     * It arrives with the page payload rather than being fetched: the form is above the
     * fold, so a second round-trip would cost SSR and flash a layout shift.
     */
    lead_form_config: null as PublicLeadFormConfig | null,
    /**
     * The POPUP custom form for this page, resolved independently of the inline one — a
     * page may show an embedded form and open a popup, and they need not be related.
     */
    lead_form_popup_config: null as PublicLeadFormConfig | null,
    /** Which tier it came from — 'page' | 'course' | 'university' | 'legacy'. */
    lead_form_source: "legacy" as string,
  },
  reducers: {
    setLeaddropFormData: (state, action) => {
      state.lead_drop_form = action.payload;
    },
    setLeaddropFormVariant: (state, action) => {
      state.legacy_variant = action.payload;
      recomputeVariant(state);
    },
    setLeadFormConfig: (
      state,
      action: {
        payload: {
          config: PublicLeadFormConfig | null;
          popupConfig?: PublicLeadFormConfig | null;
          source?: string;
        };
      },
    ) => {
      state.lead_form_config = action.payload.config;
      state.lead_form_popup_config = action.payload.popupConfig ?? null;
      state.lead_form_source = action.payload.source ?? "legacy";
      recomputeVariant(state);
    },
  },
});

export const { setLeaddropFormData, setLeaddropFormVariant, setLeadFormConfig } =
  leaddropformSlice.actions;

export default leaddropformSlice.reducer;
